import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, VAULT_PATH } from "@/lib/api";
import Header from "@/components/site/Header";
import Footer from "@/components/site/Footer";
import Seo from "@/components/site/Seo";
import { toast } from "sonner";

export default function NewsletterStudio() {
  const navigate = useNavigate();
  const [checking, setChecking] = useState(true);
  const [status, setStatus] = useState(null);
  const [preview, setPreview] = useState(null);
  const [testEmail, setTestEmail] = useState("");
  const [sendingTest, setSendingTest] = useState(false);
  const [sendingNow, setSendingNow] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        await api.get("/auth/me");
        setChecking(false);
      } catch {
        navigate(`${VAULT_PATH}/login`, { replace: true });
      }
    })();
  }, [navigate]);

  const loadStatus = async () => {
    try {
      const { data } = await api.get("/admin/newsletter/status");
      setStatus(data);
    } catch (e) { console.warn("newsletter status load failed", e); }
  };

  const loadPreview = async () => {
    try {
      const { data } = await api.get("/admin/newsletter/preview");
      setPreview(data);
    } catch (e) { console.warn("newsletter preview load failed", e); }
  };

  useEffect(() => {
    if (!checking) {
      loadStatus();
      loadPreview();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional mount-only effect; referenced values are stable module imports/constants
  }, [checking]);

  const toggleAuto = async () => {
    try {
      const { data } = await api.post("/admin/newsletter/toggle", { enabled: !status.auto_enabled });
      setStatus((s) => ({ ...s, auto_enabled: data.auto_enabled }));
      toast.success(data.auto_enabled ? "Auto-send enabled" : "Auto-send paused");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Toggle failed");
    }
  };

  const sendTest = async (e) => {
    e.preventDefault();
    if (!testEmail) return;
    setSendingTest(true);
    try {
      await api.post("/admin/newsletter/test", { email: testEmail });
      toast.success(`Test sent to ${testEmail}`);
    } catch (e2) {
      toast.error(e2?.response?.data?.detail || "Test send failed");
    }
    setSendingTest(false);
  };

  const sendNow = async () => {
    if (!window.confirm(`Send the digest to all ${status?.subscribers ?? 0} subscribers now?`)) return;
    setSendingNow(true);
    try {
      const { data } = await api.post("/admin/newsletter/send");
      toast.success(`Campaign sent · ${data.sent}/${data.total} delivered`);
      loadStatus();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    }
    setSendingNow(false);
  };

  if (checking) {
    return (
      <div className="bg-white text-[#0a0a0a] min-h-screen">
        <Header />
        <div className="mx-auto max-w-[1400px] px-6 py-20 text-center font-mono text-xs uppercase tracking-widest text-[#52525b]">
          Loading…
        </div>
      </div>
    );
  }

  return (
    <div data-testid="newsletter-studio-page" className="bg-white text-[#0a0a0a]">
      <Seo title="Newsletter Studio — Universtrend" description="Owner-only newsletter automation" />
      <Header />

      <section className="border-b border-black">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 py-12">
          <div className="flex flex-wrap items-end justify-between gap-6 mb-10">
            <div>
              <div className="overline text-[#52525b]">The Vault · Newsletter Studio</div>
              <h1 className="font-display font-extrabold text-4xl lg:text-6xl tracking-tighter leading-[0.95]">
                The Monday Index,<br /><span className="text-[#ff3b30]">on autopilot.</span>
              </h1>
            </div>
            <button
              data-testid="newsletter-back-vault"
              onClick={() => navigate(VAULT_PATH)}
              className="border border-black bg-white text-black px-5 py-2.5 overline hover:bg-[#f4f4f5]"
            >
              ← Vault
            </button>
          </div>

          {status && (
            <div className="grid grid-cols-1 md:grid-cols-4 border-t border-l border-black">
              <div className="border-r border-b border-black p-6">
                <div className="overline text-[#52525b]">Subscribers</div>
                <div data-testid="newsletter-subscribers" className="mt-3 font-display font-extrabold tracking-tighter text-5xl">
                  {status.subscribers}
                </div>
              </div>
              <div className="border-r border-b border-black p-6">
                <div className="overline text-[#52525b]">Auto-send</div>
                <button
                  data-testid="newsletter-toggle-auto"
                  onClick={toggleAuto}
                  className={`mt-3 hard-shadow border border-black px-5 py-2.5 overline ${status.auto_enabled ? "bg-[#0a7d33] text-white" : "bg-white text-black"}`}
                >
                  {status.auto_enabled ? "ON — every Monday" : "OFF — paused"}
                </button>
                <div className="mt-2 font-mono text-xs text-[#52525b]">{status.schedule}</div>
              </div>
              <div className="border-r border-b border-black p-6">
                <div className="overline text-[#52525b]">Email engine</div>
                <div data-testid="newsletter-resend-status" className={`mt-3 font-display font-extrabold tracking-tighter text-2xl ${status.resend_configured ? "text-[#0a7d33]" : "text-[#ff3b30]"}`}>
                  {status.resend_configured ? "Resend ✓" : "Key missing"}
                </div>
                {!status.resend_configured && (
                  <div className="mt-2 font-mono text-xs text-[#52525b]">Add RESEND_API_KEY to enable sending</div>
                )}
              </div>
              <div className="border-r border-b border-black p-6">
                <div className="overline text-[#52525b]">Send now</div>
                <button
                  data-testid="newsletter-send-now"
                  onClick={sendNow}
                  disabled={sendingNow || !status.resend_configured}
                  className="mt-3 hard-shadow border border-black bg-[#ff3b30] text-white px-5 py-2.5 overline disabled:opacity-40"
                >
                  {sendingNow ? "Sending…" : "Send to all →"}
                </button>
              </div>
            </div>
          )}

          <div className="mt-10 grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div>
              <div className="overline text-[#52525b] mb-4">Send a test email</div>
              <form onSubmit={sendTest} className="flex border border-black">
                <input
                  data-testid="newsletter-test-email"
                  type="email"
                  required
                  value={testEmail}
                  onChange={(e) => setTestEmail(e.target.value)}
                  placeholder="you@email.com"
                  className="flex-1 px-4 py-3 font-mono text-sm outline-none"
                />
                <button
                  data-testid="newsletter-test-send"
                  type="submit"
                  disabled={sendingTest}
                  className="bg-black text-white px-6 overline disabled:opacity-40"
                >
                  {sendingTest ? "…" : "Send test"}
                </button>
              </form>

              <div className="overline text-[#52525b] mt-10 mb-4">Recent campaigns</div>
              {!status?.recent_campaigns?.length ? (
                <div className="font-mono text-xs text-[#52525b]">No campaigns yet. The first auto-send fires next Monday.</div>
              ) : (
                <ul className="divide-y divide-black/15 border border-black">
                  {status.recent_campaigns.map((c) => (
                    <li key={c.id} className="p-4 font-mono text-xs flex justify-between gap-4" data-testid={`newsletter-campaign-${c.id}`}>
                      <span className="truncate">{c.subject}</span>
                      <span className="whitespace-nowrap text-[#52525b]">
                        {c.mode} · {c.sent}/{c.total} · {c.created_at.slice(0, 10)}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div>
              <div className="overline text-[#52525b] mb-4">
                Live preview {preview ? `· "${preview.subject}"` : ""}
              </div>
              <div className="border border-black bg-[#f4f4f5]" data-testid="newsletter-preview-frame">
                {preview ? (
                  <iframe
                    title="Newsletter preview"
                    srcDoc={preview.html}
                    className="w-full h-[720px] bg-white"
                  />
                ) : (
                  <div className="p-10 font-mono text-xs text-[#52525b]">Loading preview…</div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
